import {
  ACESFilmicToneMapping,
  Color,
  SRGBColorSpace,
  WebGLRenderer,
  type Camera,
  type Scene,
} from 'three'
import { gsap } from 'gsap'

interface RendererControllerOptions {
  canvas: HTMLCanvasElement
  clearColor: number
  exposure: number
  maxPixelRatio: number
  onContextLost: () => void
  onContextRestored: () => void
}

export class RendererController {
  public readonly renderer: WebGLRenderer
  private readonly canvas: HTMLCanvasElement
  private readonly maxPixelRatio: number
  private readonly authoredExposure: number
  private readonly handleContextLost: (event: Event) => void
  private readonly handleContextRestored: () => void
  private exposureTween: gsap.core.Tween | null = null
  private exposureResolve: (() => void) | null = null

  public constructor(options: RendererControllerOptions) {
    this.canvas = options.canvas
    this.maxPixelRatio = options.maxPixelRatio
    this.authoredExposure = options.exposure
    this.renderer = new WebGLRenderer({
      canvas: options.canvas,
      antialias: true,
      alpha: false,
      depth: true,
      stencil: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    })

    this.renderer.outputColorSpace = SRGBColorSpace
    this.renderer.toneMapping = ACESFilmicToneMapping
    this.renderer.toneMappingExposure = options.exposure
    this.renderer.shadowMap.enabled = false
    this.renderer.setClearColor(new Color(options.clearColor), 1)

    this.handleContextLost = (event: Event) => {
      event.preventDefault()
      options.onContextLost()
    }
    this.handleContextRestored = options.onContextRestored

    this.canvas.addEventListener('webglcontextlost', this.handleContextLost, false)
    this.canvas.addEventListener('webglcontextrestored', this.handleContextRestored, false)
  }

  public resize(width: number, height: number, devicePixelRatio: number): void {
    const pixelRatio = Math.min(Math.max(devicePixelRatio, 1), this.maxPixelRatio)
    this.renderer.setPixelRatio(pixelRatio)
    this.renderer.setSize(width, height, false)
  }

  public render(scene: Scene, camera: Camera): void {
    this.renderer.render(scene, camera)
  }

  public setAnimationLoop(callback: ((time: number) => void) | null): void {
    this.renderer.setAnimationLoop(callback)
  }

  public getMaxAnisotropy(): number {
    return this.renderer.capabilities.getMaxAnisotropy()
  }

  /** Remove render-list references to scene objects that were permanently detached. */
  public releaseSceneReferences(): void {
    this.renderer.renderLists.dispose()
  }

  public fadeExposureTo(intensityScale: number, durationMs: number): Promise<void> {
    this.cancelExposureFade()
    const targetScale = Math.min(1, Math.max(0, intensityScale))
    const targetExposure = this.authoredExposure * targetScale
    const duration = Math.max(0, durationMs) / 1000

    if (duration === 0) {
      this.renderer.toneMappingExposure = targetExposure
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      this.exposureResolve = resolve
      this.exposureTween = gsap.to(this.renderer, {
        toneMappingExposure: targetExposure,
        duration,
        ease: 'power2.inOut',
        onComplete: () => {
          this.exposureTween = null
          this.exposureResolve = null
          this.renderer.toneMappingExposure = targetExposure
          resolve()
        },
      })
    })
  }

  public dispose(): void {
    this.cancelExposureFade()
    this.setAnimationLoop(null)
    this.canvas.removeEventListener('webglcontextlost', this.handleContextLost)
    this.canvas.removeEventListener('webglcontextrestored', this.handleContextRestored)
    this.renderer.dispose()
  }

  private cancelExposureFade(): void {
    const resolve = this.exposureResolve
    this.exposureTween?.kill()
    this.exposureTween = null
    this.exposureResolve = null
    resolve?.()
  }
}
